import React, { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowUpRight, LineChart, MessageSquare, Search, Users, Sparkles } from "lucide-react";
import MaskedReveal from "../motion/MaskedReveal";
import CountUp from "../motion/CountUp";

export default function Hero() {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });
  const yBlob = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const yStack = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const opacityFade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  // Mouse move parallax for the dashboard stack
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 20 });
  const sy = useSpring(my, { stiffness: 60, damping: 20 });

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const nx = (e.clientX - r.left - r.width / 2) / r.width;
    const ny = (e.clientY - r.top - r.height / 2) / r.height;
    mx.set(nx * 24);
    my.set(ny * 24);
  };
  const onMouseLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section
      id="start"
      ref={containerRef}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      className="relative overflow-hidden bg-brand-primary text-white pt-32 lg:pt-40 pb-24 lg:pb-32"
      data-testid="hero-section"
    >
      {/* Background grid */}
      <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" aria-hidden />

      {/* Aurora — animated blue gradient wash */}
      <motion.div
        aria-hidden
        style={{ y: yBlob }}
        className="absolute inset-0 pointer-events-none"
      >
        <div className="aurora" />
      </motion.div>

      {/* Vertical beam */}
      <div className="beam" aria-hidden />

      {/* Grain */}
      <div className="absolute inset-0 grain pointer-events-none" aria-hidden />

      <div className="relative max-w-[90rem] mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 xl:gap-16 items-center">
          {/* Left: kinetic headline */}
          <motion.div
            style={{ y: yStack, opacity: opacityFade }}
            className="lg:col-span-8 xl:col-span-7 min-w-0"
          >
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="eyebrow text-brand-accent"
              data-testid="hero-eyebrow"
            >
              Webdesign · Social Media · KI · Recruiting
            </motion.p>

            <MaskedReveal
              as="h1"
              className="mt-6 font-display font-semibold text-white text-hero"
              lines={["Wir machen", "Unternehmen", "sichtbar."]}
              accentIndex={2}
              delay={0.25}
            />

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="mt-8 max-w-xl text-white/70 text-base sm:text-lg leading-relaxed"
              data-testid="hero-subtitle"
            >
              Von professionellen Webseiten über Social Media bis hin zu
              KI-Marketing und Recruiting begleiten wir Unternehmen in Leipzig
              und der DACH-Region auf ihrem digitalen Weg.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.05 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <a
                href="#kontakt"
                className="btn-primary"
                data-testid="hero-cta-primary"
              >
                Kostenloses Erstgespräch
                <ArrowUpRight className="h-4 w-4" />
              </a>
              <a
                href="#referenzen"
                className="btn-secondary btn-secondary--dark"
                data-testid="hero-cta-secondary"
              >
                Referenzen ansehen
              </a>
            </motion.div>

            {/* KPI strip */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.2 }}
              className="mt-16 grid grid-cols-3 max-w-lg gap-8 border-t border-white/10 pt-8"
              data-testid="hero-kpi"
            >
              {[
                { v: "150+", l: "Projekte" },
                { v: "12 J.", l: "Erfahrung" },
                { v: "94%", l: "Kundenbindung" },
              ].map((k) => (
                <div key={k.l}>
                  <div className="text-2xl lg:text-3xl font-semibold tracking-tight tabular-nums">
                    <CountUp value={k.v} />
                  </div>
                  <div className="text-xs text-white/50 uppercase tracking-widest mt-1">
                    {k.l}
                  </div>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right: dashboard stack */}
          <motion.div
            style={{ x: sx, y: sy }}
            className="relative lg:col-span-4 xl:col-span-5 h-[520px] hidden lg:block"
          >
            <FloatingStack />
          </motion.div>

          {/* Mobile compact dashboard */}
          <div className="relative lg:hidden h-[380px] mt-4">
            <FloatingStack compact />
          </div>
        </div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="mt-24 flex items-center gap-3 text-xs uppercase tracking-widest text-white/40"
        >
          <span className="h-px w-10 bg-white/20" />
          Scrollen für mehr
        </motion.div>
      </div>
    </section>
  );
}

function FloatingStack({ compact = false }) {
  const cards = [
    {
      title: "Analytics",
      icon: LineChart,
      body: "+38% Sitzungen",
      chip: "SEO",
      x: "10%",
      y: "6%",
      w: compact ? "70%" : "82%",
      delay: 0,
    },
    {
      title: "Social",
      icon: Sparkles,
      body: "5,2k neue Follower",
      chip: "LinkedIn",
      x: "0%",
      y: "38%",
      w: compact ? "62%" : "70%",
      delay: 0.6,
    },
    {
      title: "Recruiting",
      icon: Users,
      body: "142 qual. Bewerbungen",
      chip: "ClaRic",
      x: "24%",
      y: "62%",
      w: compact ? "68%" : "74%",
      delay: 1.2,
    },
    {
      title: "KI-Assistent",
      icon: MessageSquare,
      body: "Automatisiert · 24/7",
      chip: "GPT",
      x: "42%",
      y: "18%",
      w: compact ? "50%" : "58%",
      delay: 1.8,
    },
    {
      title: "Suche",
      icon: Search,
      body: "Top-3 Rankings",
      chip: "Google",
      x: "50%",
      y: "46%",
      w: compact ? "44%" : "50%",
      delay: 2.2,
    },
  ];

  return (
    <div className="absolute inset-0">
      {/* Big blurred accent behind cards */}
      <div
        aria-hidden
        className="absolute -top-10 -right-10 w-[420px] h-[420px] rounded-full"
        style={{
          background:
            "radial-gradient(circle at center, rgba(14,165,233,0.55) 0%, rgba(14,165,233,0) 70%)",
          filter: "blur(20px)",
        }}
      />

      {cards.map((c, i) => (
        <motion.div
          key={c.title}
          initial={{ opacity: 0, y: 40, rotate: -2 }}
          animate={{ opacity: 1, y: 0, rotate: 0 }}
          transition={{
            duration: 0.9,
            delay: 0.4 + i * 0.15,
            ease: [0.19, 1, 0.22, 1],
          }}
          style={{ left: c.x, top: c.y, width: c.w }}
          className="absolute"
        >
          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{
              duration: 6 + i * 0.6,
              repeat: Infinity,
              ease: "easeInOut",
              delay: c.delay,
            }}
            className="rounded-2xl border border-white/10 bg-white/[0.06] backdrop-blur-xl shadow-[0_25px_80px_-20px_rgba(14,165,233,0.35)] p-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-white/80 text-[11px] uppercase tracking-widest">
                <c.icon className="h-3.5 w-3.5 text-brand-accent" />
                {c.title}
              </div>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-accent/20 text-brand-accent">
                {c.chip}
              </span>
            </div>
            <div className="mt-3 text-white text-sm font-medium tracking-tight">
              {c.body}
            </div>
            <div className="mt-3 grid grid-cols-6 gap-1">
              {Array.from({ length: 6 }).map((_, k) => (
                <div
                  key={k}
                  className="h-1.5 rounded-full"
                  style={{
                    background:
                      k < 4
                        ? "linear-gradient(90deg,#0EA5E9,#38BDF8)"
                        : "rgba(255,255,255,0.15)",
                  }}
                />
              ))}
            </div>
          </motion.div>
        </motion.div>
      ))}
    </div>
  );
}
